import React, { FormEvent, useEffect, useRef } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react';

import AuthPageStore from './AuthPageStore';
import ROUTES from '@app/src/common/constants/routeUrls';
import routerStore from '@app/src/common/store/RouterStore';
import AuthStore from '@app/src/common/store/AuthStore';
import { SignInFormFields } from './SignInFormFields';

import { Input, CustomButton, Modifiers } from '@fw-nextgen/fw-components';
import AuthWindow from '../components/AuthWindow';
import AuthWindowLogo from '../components/AuthWindowLogo';
import MfaCodeStep from './components/mfa-code-step';

const AuthPage = () => {
  const { current: store } = useRef(new AuthPageStore());
  const { formatMessage } = useIntl();

  useEffect(() => {
    return () => {
      store.form.clear();
      store.clearChallenge();
    };
  }, []);

  const setEmail = (value: string) => store.form.email.setValue(value);

  const setPassword = (value: string) => store.form.password.setValue(value);

  const signIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    store.signIn(store.form.email.value, store.form.password.value);
  };

  if (AuthStore.isUserAuthValid && routerStore.routes.length) {
    return <Redirect to={ROUTES.homePage.path} />;
  }
  const { loading, form, challenge } = store;

  if (challenge) {
    return (
      <AuthWindow>
        <MfaCodeStep store={challenge} onBack={store.clearChallenge} />
      </AuthWindow>
    );
  }

  return (
    <AuthWindow>
      <form className="auth-window__form" onSubmit={signIn}>
        <AuthWindowLogo />
        <h5 className="auth-window__heading">
          <FormattedMessage id="auth.form.heading" />
        </h5>
        <div>
          <Input
            disabled={loading}
            {...SignInFormFields.email}
            classNameFormField="auth-window__form-field_email"
            value={form.email.value}
            error={form.email.isTouched ? form.email.error : undefined}
            onChange={setEmail}
            placeholder={formatMessage({ id: 'auth.form.email.placeholder' })}
          />
          <Input
            disabled={loading}
            type="password"
            {...SignInFormFields.password}
            value={form.password.value}
            error={form.password.isTouched ? form.password.error : undefined}
            onChange={setPassword}
            placeholder={formatMessage({ id: 'auth.form.password.label' })}
          />

          <div className="auth-window__toolbar">
            <Link
              to={ROUTES.forgotPasswordPage.path}
              className="auth-window__link"
            >
              <FormattedMessage id="auth.form.forgot.pass.link.label" />
            </Link>
          </div>
        </div>

        <CustomButton
          loading={loading}
          id="ok__btn"
          modifiers={[Modifiers.SMALL]}
          className="auth-window__button"
          value="button.login"
          type="submit"
          disabled={!form.isValid}
          actionText="spinner.text.login"
        />
      </form>
    </AuthWindow>
  );
};

export default observer(AuthPage);
